import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Text,
  Button,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { useRef } from 'react'
import { Dataset } from '../../stores/dataStore'
import { useDataStore } from '../../stores/dataStore'
import Papa from 'papaparse'

interface DatasetPreviewProps {
  dataset: Dataset
  isOpen: boolean
  onClose: () => void
}

interface PreviewData {
  columns: string[]
  rows: Record<string, any>[]
}

const MAX_COLUMNS = 20
const MAX_ROWS = 50

export const DatasetPreview = ({ dataset, isOpen, onClose }: DatasetPreviewProps) => {
  const { removeDataset } = useDataStore()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const handleDelete = () => {
    removeDataset(dataset.id)
    onDeleteClose()
    onClose()
  }

  const getPreviewData = (): PreviewData => {
    try {
      if (dataset.type === 'csv') {
        console.log('Preview dataset:', {
          hasMetadata: !!dataset.metadata,
          hasAttributes: !!dataset.metadata?.attributes,
          hasParsedData: !!dataset.metadata?.attributes?.parsedData,
          hasFields: !!dataset.metadata?.attributes?.fields,
          fieldsCount: dataset.metadata?.attributes?.fields?.length,
          parsedDataCount: dataset.metadata?.attributes?.parsedData?.length,
          parsedDataSample: dataset.metadata?.attributes?.parsedData?.[0],
          fields: dataset.metadata?.attributes?.fields
        })

        // Use pre-parsed data if available
        if (Array.isArray(dataset.metadata?.attributes?.parsedData) && 
            Array.isArray(dataset.metadata?.attributes?.fields)) {
          console.log('Using pre-parsed data')
          return {
            columns: dataset.metadata.attributes.fields.slice(0, MAX_COLUMNS),
            rows: dataset.metadata.attributes.parsedData
          }
        }

        // Fallback to parsing the data
        if (typeof dataset.data === 'string') {
          console.log('Parsing raw data')
          const results = Papa.parse(dataset.data, { 
            header: true,
            preview: MAX_ROWS + 1
          })
          
          const columns = (results.meta.fields || []).slice(0, MAX_COLUMNS)
          const rows = results.data.slice(0, MAX_ROWS).map(row => {
            if (row && typeof row === 'object') {
              return columns.reduce((acc, col) => ({
                ...acc,
                [col]: (row as Record<string, unknown>)[col]
              }), {} as Record<string, unknown>)
            }
            return {} as Record<string, unknown>
          })
          return { columns, rows }
        }

        console.log('No valid data found:', {
          dataType: typeof dataset.data,
          data: dataset.data,
          metadata: dataset.metadata
        })
        throw new Error('No valid data found')
      }
      return { columns: [], rows: [] }
    } catch (error) {
      console.error('Error parsing dataset:', error)
      return { columns: [], rows: [] }
    }
  }

  const { columns, rows } = getPreviewData()

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="90vw">
          <ModalHeader>
            {dataset.name}
            <Text fontSize="sm" color="gray.500">
              Preview showing {rows.length} of {dataset.metadata?.attributes?.rowCount || 0} rows
              {columns.length < (dataset.metadata?.variables?.length || 0) && 
                ` and ${columns.length} of ${dataset.metadata?.variables?.length} columns`}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Box overflowX="auto">
              {columns.length > 0 ? (
                <Table size="sm" variant="striped">
                  <Thead>
                    <Tr>
                      {columns.map((column: string, idx: number) => (
                        <Th key={idx}>{column}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((row: Record<string, any>, idx: number) => (
                      <Tr key={idx}>
                        {columns.map((column: string, colIdx: number) => (
                          <Td key={colIdx}>{row[column]}</Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Text>No preview available for this file type yet.</Text>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={onDeleteOpen}>
              Delete Dataset
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Dataset
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{dataset.name}"? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
} 